class HotStopCollectionTabs {
  constructor(section){
    this.section = section;
    this.buttons = [...section.querySelectorAll('[data-hotstop-tab-button]')];
    this.panels = [...section.querySelectorAll('[data-hotstop-tab-panel]')];
    this.transitionMs = 280;
    this.panelTimers = new WeakMap();

    this.panels.forEach(panel => {
      panel.setAttribute('aria-hidden', panel.hidden ? 'true' : 'false');
    });

    this.buttons.forEach(button => {
      button.addEventListener('click', () => this.activate(button));
    });
  }

  prefersReducedMotion(){
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  clearPanelTimer(panel){
    const timer = this.panelTimers.get(panel);
    if(timer){
      window.clearTimeout(timer);
      this.panelTimers.delete(panel);
    }
  }

  activate(activeButton){
    const targetId = activeButton.dataset.hotstopTabTarget;
    const incomingPanel = this.panels.find(panel => panel.id === targetId);

    if(!incomingPanel) return;

    const alreadyActive = activeButton.classList.contains('is-active') && incomingPanel.classList.contains('is-active');
    if(alreadyActive) return;

    const reduceMotion = this.prefersReducedMotion();

    this.buttons.forEach(button => {
      const isActive = button === activeButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach(panel => {
      this.clearPanelTimer(panel);

      const isIncoming = panel === incomingPanel;
      panel.classList.remove('is-entering', 'is-animating-in');

      if(isIncoming){
        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'false');

        if(reduceMotion){
          panel.classList.add('is-active');
          panel.classList.remove('is-leaving');
          return;
        }

        panel.classList.remove('is-leaving');
        panel.classList.add('is-entering');

        // Force the browser to register the entering state before animating in.
        void panel.offsetHeight;

        window.requestAnimationFrame(() => {
          panel.classList.add('is-active', 'is-animating-in');
          panel.classList.remove('is-entering');

          const timer = window.setTimeout(() => {
            panel.classList.remove('is-animating-in');
            this.panelTimers.delete(panel);
          }, this.transitionMs + 180);

          this.panelTimers.set(panel, timer);
        });

        return;
      }

      const wasVisible = !panel.hidden || panel.classList.contains('is-active') || panel.classList.contains('is-leaving');
      panel.setAttribute('aria-hidden', 'true');

      if(!wasVisible){
        panel.hidden = true;
        panel.classList.remove('is-active', 'is-leaving');
        return;
      }

      if(reduceMotion){
        panel.hidden = true;
        panel.classList.remove('is-active', 'is-leaving');
        return;
      }

      panel.classList.remove('is-active');
      panel.classList.add('is-leaving');

      const timer = window.setTimeout(() => {
        panel.hidden = true;
        panel.classList.remove('is-leaving');
        this.panelTimers.delete(panel);
      }, this.transitionMs);

      this.panelTimers.set(panel, timer);
    });

    try{
      activeButton.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', inline:'center', block:'nearest' });
    }catch(e){}
  }
}

function initHotStopCollectionTabs(scope = document){
  scope.querySelectorAll('[data-hotstop-collection-tabs]').forEach(section => {
    if(section.dataset.hotstopCollectionTabsReady === 'true') return;
    section.dataset.hotstopCollectionTabsReady = 'true';
    new HotStopCollectionTabs(section);
  });
}

document.addEventListener('DOMContentLoaded', () => initHotStopCollectionTabs());
document.addEventListener('shopify:section:load', event => initHotStopCollectionTabs(event.target));

/* HOTSTOP ISSUE 21B26 — HIDDEN NATIVE TRAY CONTEXT */
(function(){
  const MOBILE_QUERY = '(max-width: 749px)';
  let isProgrammaticTrayClick = false;

  function isMobile(){
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  }

  function closestVisibleCard(target){
    return target && target.closest
      ? target.closest('.hotstop-collection-tabs-section article.hotstop-tab-product-card')
      : null;
  }

  function isInsideVisibleAdd(target){
    return Boolean(target && target.closest && target.closest('.hotstop-tab-product-card__quick-add'));
  }

  function findTrayTrigger(card){
    if(!card) return null;

    /*
      Use only the hidden Horizon product-card context.
      Never use the visible compact plus CTA as the tray source.
    */
    const trayCard = card.querySelector('product-card.hotstop-tab-product-card__tray-card');
    if(!trayCard) return null;

    if(!trayCard.querySelector('[ref="productCardLink"]')) return null;

    const selectors = [
      '.quick-add__button--choose',
      'button[on\\:click*="quick-add-component/handleClick"]',
      'quick-add-component button[type="button"]'
    ];

    for(const selector of selectors){
      const el = trayCard.querySelector(selector);
      if(el) return el;
    }

    return null;
  }

  document.addEventListener('click', function(event){
    if(!isMobile()) return;
    if(isProgrammaticTrayClick) return;

    const card = closestVisibleCard(event.target);
    if(!card) return;

    /*
      Plus CTA keeps direct add-to-cart behavior.
    */
    if(isInsideVisibleAdd(event.target)) return;

    const trigger = findTrayTrigger(card);
    if(!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    isProgrammaticTrayClick = true;
    try{
      trigger.click();
    }finally{
      window.setTimeout(function(){
        isProgrammaticTrayClick = false;
      }, 120);
    }
  }, true);
})();
/* END HOTSTOP ISSUE 21B26 */

/* HOTSTOP ISSUE 21B37 — REAL IMG ICON DESKTOP HOVER CTA */
(function(){
  function enhanceDesktopHoverButtons(root){
    const scope = root || document;
    const buttons = scope.querySelectorAll(
      '.hotstop-collection-tabs-section article.hotstop-tab-product-card .hotstop-tab-product-card__desktop-hover-add .quick-add__button--add.add-to-cart-button,' +
      '.hotstop-collection-tabs-section article.hotstop-tab-product-card .hotstop-tab-product-card__desktop-hover-add add-to-cart-component > button.quick-add__button--add'
    );

    buttons.forEach(function(button){
      if(button.dataset.hotstopDesktopHoverEnhanced === 'true') return;

      const iconSrc = button.closest('.hotstop-collection-tabs-section')?.dataset.hotstopDesktopAddIcon;
      if(!iconSrc) return;

      button.dataset.hotstopDesktopHoverEnhanced = 'true';

      const nativeChildren = Array.from(button.children);
      nativeChildren.forEach(function(child){
        child.classList.add('hotstop-desktop-native-hidden');
        child.setAttribute('aria-hidden', 'true');
      });

      const icon = document.createElement('img');
      icon.className = 'hotstop-desktop-hover-add__icon';
      icon.src = iconSrc;
      icon.alt = '';
      icon.setAttribute('aria-hidden', 'true');
      icon.loading = 'eager';
      icon.decoding = 'async';

      const label = document.createElement('span');
      label.className = 'hotstop-desktop-hover-add__label';
      label.textContent = 'Add';
      label.setAttribute('aria-hidden', 'true');

      button.appendChild(icon);
      button.appendChild(label);
      button.setAttribute('aria-label', 'Add to cart');
    });
  }

  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', function(){ enhanceDesktopHoverButtons(document); });
  }else{
    enhanceDesktopHoverButtons(document);
  }

  document.addEventListener('shopify:section:load', function(event){
    enhanceDesktopHoverButtons(event.target || document);
  });

  const observer = new MutationObserver(function(mutations){
    for(const mutation of mutations){
      if(mutation.addedNodes && mutation.addedNodes.length){
        enhanceDesktopHoverButtons(document);
        break;
      }
    }
  });

  observer.observe(document.documentElement, { childList:true, subtree:true });
})();
/* END HOTSTOP ISSUE 21B37 */
